import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Modal, Table, Spin, Select } from 'antd';
import { CmsUserHistory, PaginatedList } from '../../types';
import { Text } from '../../utils/text';
import { Pagination } from '../pagination';
import { TEN_PER_PAGE, getCmsHistoryActionLabel } from '../../utils/const';
import style from './cms-user-history-modal.module.scss';
import { formatDate } from './utils';

export interface CmsUserHistoryModalProps {
    open: boolean;
    cmsUserHistory?: CmsUserHistory[];
    onClose: () => void;
    fetchHistory?: (
        page: number,
        perPage: number,
        action?: string
    ) => Promise<void>;
    historyData?: PaginatedList<CmsUserHistory> | null;
    isFetching?: boolean;
    actionFilter?: string;
    onActionFilterChange?: (action: string | undefined) => void;
    availableActions?: readonly string[];
}

const columns = [
    {
        title: Text.cmsUserhistoryComponent.columns.email,
        dataIndex: 'email',
        key: 'email',
    },
    {
        title: Text.cmsUserhistoryComponent.columns.action,
        dataIndex: 'action',
        key: 'action',
        render: (_: unknown, record: CmsUserHistory) => (
            <div>{getCmsHistoryActionLabel(record.action)}</div>
        ),
    },
    {
        title: Text.cmsUserhistoryComponent.columns.date,
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (_: unknown, record: CmsUserHistory) => (
            <div>{formatDate(record.createdAt)}</div>
        ),
    },
];

export const CmsUserHistoryModal = observer(
    ({
        open,
        cmsUserHistory,
        onClose,
        fetchHistory,
        historyData,
        isFetching = false,
        actionFilter,
        onActionFilterChange,
        availableActions,
    }: CmsUserHistoryModalProps) => {
        const [page, setPage] = useState(0);
        const [perPage, setPerPage] = useState(TEN_PER_PAGE);

        const hasPagination = !!fetchHistory && !!historyData;
        const displayHistory = useMemo(
            () =>
                hasPagination
                    ? historyData?.results ?? []
                    : cmsUserHistory ?? [],
            [hasPagination, historyData?.results, cmsUserHistory]
        );

        const fetchHistoryRef = useRef(fetchHistory);
        const prevActionFilterRef = useRef<string | undefined>(actionFilter);

        useEffect(() => {
            fetchHistoryRef.current = fetchHistory;
        }, [fetchHistory]);

        const prevHistoryDataRef = useRef(historyData);

        useEffect(() => {
            if (open && fetchHistoryRef.current) {
                const apiPage = page + 1;
                const actionFilterChanged =
                    prevActionFilterRef.current !== actionFilter;
                prevActionFilterRef.current = actionFilter;
                prevHistoryDataRef.current = historyData;

                if (
                    !historyData ||
                    historyData.page !== apiPage ||
                    historyData.perPage !== perPage ||
                    actionFilterChanged
                ) {
                    fetchHistoryRef.current(page, perPage, actionFilter);
                }
            }
        }, [open, page, perPage, actionFilter, historyData]);

        const handleClose = useCallback(() => {
            setPage(0);
            setPerPage(TEN_PER_PAGE);
            onClose();
        }, [onClose]);

        const handlePageChange = useCallback((newPage: number) => {
            setPage(newPage);
        }, []);

        const handlePageSizeChange = useCallback((newPerPage: number) => {
            setPerPage(newPerPage);
            setPage(0);
        }, []);

        const handleActionFilterChange = useCallback(
            (value: string | undefined) => {
                setPage(0);
                if (onActionFilterChange) {
                    onActionFilterChange(value);
                }
            },
            [onActionFilterChange]
        );

        const actionOptions = useMemo(
            () =>
                availableActions
                    ? [
                          { label: 'All', value: undefined },
                          ...availableActions.map((action) => ({
                              label: getCmsHistoryActionLabel(action),
                              value: action,
                          })),
                      ]
                    : [],
            [availableActions]
        );

        const modalTitle = useMemo(
            () => (
                <div className={style.modal_title}>
                    {Text.cmsUserhistoryComponent.title}
                </div>
            ),
            []
        );

        return (
            <Modal
                open={open}
                title={modalTitle}
                footer={null}
                onCancel={handleClose}
                className={style.custom_modal}
                width={1000}
                destroyOnClose={false}
            >
                <div className={style.modal_content}>
                    {isFetching ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            <Spin />
                        </div>
                    ) : (
                        <>
                            {(availableActions || hasPagination) && (
                                <div className={style.filters_pagination_row}>
                                    {availableActions && (
                                        <div className={style.filter_container}>
                                            <Select
                                                style={{ width: 200 }}
                                                placeholder="Filter by action"
                                                value={actionFilter}
                                                onChange={
                                                    handleActionFilterChange
                                                }
                                                options={actionOptions}
                                                allowClear
                                            />
                                        </div>
                                    )}
                                    {hasPagination && (
                                        <div
                                            className={
                                                style.pagination_container
                                            }
                                        >
                                            <Pagination
                                                page={
                                                    historyData.page
                                                        ? historyData.page - 1
                                                        : 0
                                                }
                                                pageSize={historyData.perPage}
                                                total={historyData.total}
                                                onPageChange={handlePageChange}
                                                onPageSizeChange={
                                                    handlePageSizeChange
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                            <Table
                                dataSource={displayHistory}
                                columns={columns}
                                pagination={false}
                                className={style.custom_table}
                                rowKey={(record, index) =>
                                    `${record.createdAt}-${index}`
                                }
                            />
                        </>
                    )}
                </div>
            </Modal>
        );
    }
);
